import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, Buildings, Desktop, Users, FolderSimple, CircleNotch, Plus, Trash,
  Clock, ShieldCheck, User, Wrench, Copy, Check, MagnifyingGlass, MapPin,
  AirplaneTilt, Train, Moon, Star, X, Funnel, Globe, Flag, MapTrifold, GlobeHemisphereWest
} from '@phosphor-icons/react';

const roleColors = { superadmin: 'bg-red-500/10 text-red-400 border-red-500/20', admin: 'bg-blue-500/10 text-blue-400 border-blue-500/20', technician: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', viewer: 'bg-[#52525b]/20 text-[#a1a1aa] border-white/10' };
const roleIcons = { superadmin: ShieldCheck, admin: ShieldCheck, technician: Wrench, viewer: User };
const poiIcons = { airport: AirplaneTilt, train_station: Train, '24_7': Moon, important: Star };
const poiColors = { airport: 'text-blue-400 bg-blue-500/10', train_station: 'text-green-400 bg-green-500/10', '24_7': 'text-purple-400 bg-purple-500/10', important: 'text-yellow-400 bg-yellow-500/10' };

export default function TenantDetailPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { isSuperadmin } = useAuth();
  const { t } = useLanguage();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ continent: '', country: '', state: '', city: '', poi_type: '', status: '' });
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'viewer' });
  const [editDeviceOpen, setEditDeviceOpen] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showUsers, setShowUsers] = useState(false);

  const roleLabels = { superadmin: t('role.superadmin'), admin: t('role.admin'), technician: t('role.technician'), viewer: t('role.viewer') };
  const poiLabels = { airport: t('poi.airport'), train_station: t('poi.trainStation'), '24_7': t('poi.247'), important: t('poi.important') };

  const fetchTenant = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filters.continent) params.continent = filters.continent;
      if (filters.country) params.country = filters.country;
      if (filters.state) params.state = filters.state;
      if (filters.city) params.city = filters.city;
      if (filters.poi_type) params.poi_type = filters.poi_type;
      if (filters.status) params.status = filters.status;
      const res = await api.get(`/tenants/${tenantId}`, { params });
      setTenant(res.data);
    } catch (e) {
      toast.error(t('td.notFound'));
      navigate('/tenants');
    } finally { setLoading(false); }
  }, [tenantId, search, filters, navigate]);

  useEffect(() => {
    const timeout = setTimeout(fetchTenant, search ? 300 : 0);
    return () => clearTimeout(timeout);
  }, [fetchTenant]);

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) { toast.error(t('td.fillAll')); return; }
    setSaving(true);
    try {
      await api.post('/users', { ...newUser, tenant_id: tenantId });
      toast.success(t('td.userCreated'));
      setAddUserOpen(false);
      setNewUser({ name: '', email: '', password: '', role: 'viewer' });
      fetchTenant();
    } catch (e) { toast.error(e.response?.data?.detail || t('common.error')); }
    finally { setSaving(false); }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`"${userName}" ${t('tenants.deleteConfirm')}`)) return;
    try { await api.delete(`/users/${userId}`); toast.success(t('td.userDeleted')); fetchTenant(); }
    catch (e) { toast.error(t('common.error')); }
  };

  const handleCopyId = () => { navigator.clipboard.writeText(tenantId); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const openEditDevice = (device) => {
    setEditForm({
      location_continent: device.location_continent || '',
      location_country: device.location_country || '',
      location_state: device.location_state || '',
      location_city: device.location_city || '',
      location_address: device.location_address || '',
      station_code: device.station_code || '',
      poi_type: device.poi_type || '',
      notes: device.notes || '',
    });
    setEditDeviceOpen(device.id);
  };

  const handleSaveDevice = async () => {
    setSaving(true);
    try {
      const data = {};
      Object.entries(editForm).forEach(([k, v]) => { if (v) data[k] = v; });
      await api.patch(`/device/${editDeviceOpen}`, data);
      toast.success(t('td.deviceUpdated'));
      setEditDeviceOpen(null);
      fetchTenant();
    } catch (e) { toast.error(t('common.error')); }
    finally { setSaving(false); }
  };

  const clearFilters = () => setFilters({ continent: '', country: '', state: '', city: '', poi_type: '', status: '' });
  const hasActiveFilters = Object.values(filters).some(v => v);

  if (loading) return <div className="flex items-center justify-center h-64"><CircleNotch size={32} className="animate-spin text-[#D50C2D]" /></div>;
  if (!tenant) return null;

  const onlinePercent = tenant.device_count > 0 ? Math.round((tenant.online_count / tenant.device_count) * 100) : 0;
  const filterOpts = tenant.filter_options || {};

  const FilterSelect = ({ value, onChange, placeholder, options, icon: Icon }) => (
    <Select value={value || 'all'} onValueChange={v => onChange(v === 'all' ? '' : v)}>
      <SelectTrigger className="bg-[#0A0A0B] border-white/10 h-8 text-xs w-full">
        <div className="flex items-center gap-1.5 truncate">
          {Icon && <Icon size={11} className="text-[#52525b] shrink-0" />}
          <SelectValue placeholder={placeholder} />
        </div>
      </SelectTrigger>
      <SelectContent className="bg-[#141416] border-white/10">
        <SelectItem value="all">{placeholder}</SelectItem>
        {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div data-testid="tenant-detail-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
        <Link to="/tenants" className="p-2 text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded transition-colors" data-testid="back-to-tenants">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex items-center gap-3 flex-1">
          <div className="w-12 h-12 bg-[#1C1C1F] rounded-lg flex items-center justify-center">
            <Buildings size={24} className="text-[#D50C2D]" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-white tracking-tight">{tenant.name}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="tenant-badge">{tenant.code}</span>
              <span className={`status-badge ${tenant.is_active ? 'online' : 'offline'}`}>{tenant.is_active ? t('common.active') : t('common.inactive')}</span>
              <button onClick={handleCopyId} className="text-xs text-[#52525b] hover:text-[#a1a1aa] flex items-center gap-1 transition-colors">
                {copied ? <><Check size={10} className="text-green-400" /> {t('common.copied')}</> : <><Copy size={10} /> ID</>}
              </button>
            </div>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowUsers(!showUsers)} className="border-white/10" data-testid="toggle-users-btn">
          <Users size={14} /> {tenant.user_count} {t('td.users')}
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="bg-[#141416] border border-white/10 rounded p-4 hover:border-[#D50C2D] transition-colors cursor-default">
          <div className="flex items-center gap-2 text-[#52525b] text-xs mb-1"><Desktop size={14} /> {t('td.devices')}</div>
          <div className="text-2xl font-bold text-white">{tenant.device_count}</div>
          <div className="text-xs text-green-400">{tenant.online_count} {t('td.online')} ({onlinePercent}%)</div>
        </div>
        <div className="bg-[#141416] border border-white/10 rounded p-4 hover:border-[#D50C2D] transition-colors cursor-default">
          <div className="flex items-center gap-2 text-[#52525b] text-xs mb-1"><Users size={14} /> {t('td.users')}</div>
          <div className="text-2xl font-bold text-white">{tenant.user_count}</div>
        </div>
        <div className="bg-[#141416] border border-white/10 rounded p-4 hover:border-[#D50C2D] transition-colors cursor-default">
          <div className="flex items-center gap-2 text-[#52525b] text-xs mb-1"><FolderSimple size={14} /> {t('td.groups')}</div>
          <div className="text-2xl font-bold text-white">{tenant.group_count}</div>
        </div>
        <div className="bg-[#141416] border border-white/10 rounded p-4 hover:border-[#D50C2D] transition-colors cursor-default">
          <div className="flex items-center gap-2 text-[#52525b] text-xs mb-1"><Clock size={14} /> {t('common.created')}</div>
          <div className="text-sm font-medium text-white mt-1">{new Date(tenant.created_at).toLocaleDateString('de-DE')}</div>
        </div>
      </div>

      {showUsers && (
        <div className="bg-[#141416] border border-white/10 rounded mb-5 animate-in slide-in-from-top-2">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <h2 className="font-heading font-semibold text-white text-sm flex items-center gap-2"><Users size={16} className="text-[#D50C2D]" /> {t('td.users')}</h2>
            <div className="flex items-center gap-2">
              {isSuperadmin && <Button size="sm" onClick={() => setAddUserOpen(true)} className="bg-[#D50C2D] hover:bg-[#f21d41] h-7 px-2 text-xs" data-testid="add-user-btn"><Plus size={12} /> {t('common.new')}</Button>}
              <button onClick={() => setShowUsers(false)} className="p-1 text-[#52525b] hover:text-white"><X size={14} /></button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 p-3">
            {tenant.users?.map(u => {
              const RoleIcon = roleIcons[u.role] || User;
              return (
                <div key={u.id} className="flex items-center gap-3 p-3 bg-[#0A0A0B] rounded border border-white/5 group" data-testid={`tenant-user-${u.id}`}>
                  <div className="w-8 h-8 bg-[#141416] rounded-full flex items-center justify-center shrink-0"><RoleIcon size={14} className="text-[#a1a1aa]" /></div>
                  <div className="flex-1 min-w-0">
                    <span className="text-white text-sm font-medium block truncate">{u.name}</span>
                    <span className="text-[#52525b] text-xs block truncate">{u.email}</span>
                  </div>
                  <span className={`text-xs px-1.5 py-0.5 rounded border shrink-0 ${roleColors[u.role] || roleColors.viewer}`}>{roleLabels[u.role] || u.role}</span>
                  {isSuperadmin && u.role !== 'superadmin' && (
                    <button onClick={() => handleDeleteUser(u.id, u.name)} className="p-1 text-[#52525b] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all shrink-0"><Trash size={14} /></button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-[#141416] border border-white/10 rounded p-3 mb-4 space-y-2">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525b]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('td.searchPlaceholder')}
            className="pl-9 bg-[#0A0A0B] border-white/10 text-white placeholder:text-[#52525b] h-9 text-sm" data-testid="tenant-search" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 items-center">
          <FilterSelect value={filters.continent} onChange={v => setFilters(p => ({...p, continent: v}))} placeholder={t('td.continent')} options={filterOpts.continents || []} icon={GlobeHemisphereWest} />
          <FilterSelect value={filters.country} onChange={v => setFilters(p => ({...p, country: v}))} placeholder={t('td.country')} options={filterOpts.countries || []} icon={Flag} />
          <FilterSelect value={filters.state} onChange={v => setFilters(p => ({...p, state: v}))} placeholder={t('td.state')} options={filterOpts.states || []} icon={MapTrifold} />
          <FilterSelect value={filters.city} onChange={v => setFilters(p => ({...p, city: v}))} placeholder={t('td.city')} options={filterOpts.cities || []} icon={MapPin} />
          <FilterSelect value={filters.poi_type} onChange={v => setFilters(p => ({...p, poi_type: v}))} placeholder={t('td.poiType')} options={filterOpts.poi_types || []} icon={Star} />
          <FilterSelect value={filters.status} onChange={v => setFilters(p => ({...p, status: v}))} placeholder={t('common.status')} options={['online', 'offline']} icon={Globe} />
          {hasActiveFilters ? (
            <button onClick={clearFilters} className="text-xs text-[#D50C2D] hover:text-[#f21d41] flex items-center justify-center gap-1 h-9" data-testid="clear-filters"><X size={12} /> {t('common.reset')}</button>
          ) : (
            <div className="hidden lg:block" />
          )}
        </div>
        {(search || hasActiveFilters) && (
          <div className="text-xs text-[#52525b]">{tenant.filtered_count} {t('td.ofDevices')} {tenant.device_count} {t('td.devicesLabel')}</div>
        )}
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-semibold text-white text-sm flex items-center gap-2">
            <Desktop size={16} className="text-[#D50C2D]" /> {t('td.devices')} ({tenant.filtered_count ?? tenant.device_count})
          </h2>
        </div>
        {tenant.devices?.length === 0 ? (
          <div className="text-center py-12 bg-[#141416] border border-white/10 rounded">
            <Desktop size={40} className="mx-auto text-[#52525b] mb-3" />
            <p className="text-[#a1a1aa] text-sm">{search || hasActiveFilters ? t('td.noDevicesFilter') : t('td.noDevicesRegistered')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="tenant-devices-grid">
            {tenant.devices?.map(d => {
              const PoiIcon = d.poi_type ? poiIcons[d.poi_type] : null;
              return (
                <div key={d.id}
                  className="bg-[#141416] border border-white/10 rounded-lg overflow-hidden hover:border-[#D50C2D] transition-colors group"
                  data-testid={`tenant-device-${d.id}`}>
                  <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => navigate(`/devices/${d.id}`)}>
                    <Desktop size={18} className="text-[#52525b] group-hover:text-[#D50C2D] transition-colors shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-white font-medium text-sm block truncate">{d.computer_name}</span>
                      {d.station_code && <span className="text-[#52525b] text-xs font-mono">{d.station_code}</span>}
                    </div>
                    <span className={`status-badge text-xs ${d.is_online ? 'online' : 'offline'}`}>
                      <span className={`status-dot ${d.is_online ? 'online' : 'offline'}`} />
                      {d.is_online ? t('common.online') : t('common.offline')}
                    </span>
                  </div>

                  <div className="px-4 pb-3 space-y-2 border-t border-white/5 pt-2">
                    {(d.location_city || d.location_country || d.location_address) && (
                      <div className="flex items-start gap-2">
                        <MapPin size={13} className="text-[#D50C2D] mt-0.5 shrink-0" />
                        <div className="text-xs min-w-0">
                          {d.location_address && <span className="text-[#a1a1aa] block truncate">{d.location_address}</span>}
                          <span className="text-[#52525b] block truncate">
                            {[d.location_city, d.location_state, d.location_country].filter(Boolean).join(', ')}
                          </span>
                          {d.location_continent && <span className="text-[#52525b]/60 text-[10px]">{d.location_continent}</span>}
                        </div>
                      </div>
                    )}
                    {!d.location_city && !d.location_country && !d.location_address && (
                      <div className="flex items-center gap-2 text-[#52525b]/50 text-xs">
                        <MapPin size={13} /> <span>{t('td.noLocation')}</span>
                      </div>
                    )}

                    {d.poi_type && PoiIcon && (
                      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs ${poiColors[d.poi_type] || 'text-[#a1a1aa] bg-white/5'}`}>
                        <PoiIcon size={12} /> {poiLabels[d.poi_type] || d.poi_type}
                      </div>
                    )}

                    {d.notes && (
                      <p className="text-[#a1a1aa] text-xs bg-[#0A0A0B] rounded px-2 py-1.5 border border-white/5 line-clamp-2">{d.notes}</p>
                    )}

                    <div className="flex items-center justify-between pt-1">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <div className="flex items-center gap-1">
                          <span className={`led-indicator ${d.teamviewer_id ? 'led-green' : 'led-off'}`} />
                          <span className="text-[10px] text-[#52525b]">TV</span>
                          {d.teamviewer_id && <span className="text-[10px] font-mono text-[#a1a1aa]">{d.teamviewer_id}</span>}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={`led-indicator ${d.rustdesk_id ? 'led-green' : 'led-off'}`} />
                          <span className="text-[10px] text-[#52525b]">RD</span>
                          {d.rustdesk_id && <span className="text-[10px] font-mono text-[#a1a1aa]">{d.rustdesk_id}</span>}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={`led-indicator ${d.anydesk_id ? 'led-green' : 'led-off'}`} />
                          <span className="text-[10px] text-[#52525b]">AD</span>
                          {d.anydesk_id && <span className="text-[10px] font-mono text-[#a1a1aa]">{d.anydesk_id}</span>}
                        </div>
                        {d.ip_address && <span className="text-[#52525b] text-[10px] font-mono">{d.ip_address}</span>}
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); openEditDevice(d); }}
                        className="text-[#52525b] hover:text-[#D50C2D] text-xs opacity-0 group-hover:opacity-100 transition-all" data-testid={`edit-device-loc-${d.id}`}>
                        {t('common.edit')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {tenant.groups?.length > 0 && (
        <div className="bg-[#141416] border border-white/10 rounded mb-6">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
            <FolderSimple size={16} className="text-[#D50C2D]" />
            <h2 className="font-heading font-semibold text-white text-sm">{t('td.groups')} ({tenant.group_count})</h2>
          </div>
          <div className="divide-y divide-white/5">
            {tenant.groups.map(g => (
              <div key={g.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.02] cursor-pointer" onClick={() => navigate('/groups')}>
                <FolderSimple size={14} className="text-[#52525b]" />
                <span className="text-white text-sm font-medium flex-1">{g.name}</span>
                <span className="text-[#52525b] text-xs">{g.device_ids?.length || 0} {t('td.devices')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!editDeviceOpen} onOpenChange={() => setEditDeviceOpen(null)}>
        <DialogContent className="bg-[#141416] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{t('td.editLocation')}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#a1a1aa] text-xs">{t('loc.continent')}</Label>
                <Select value={editForm.location_continent || 'none'} onValueChange={v => setEditForm(p => ({...p, location_continent: v === 'none' ? '' : v}))}>
                  <SelectTrigger className="bg-[#0A0A0B] border-white/10 mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141416] border-white/10">
                    <SelectItem value="none">--</SelectItem>
                    <SelectItem value="Europa">{t('cont.europe')}</SelectItem>
                    <SelectItem value="Nordamerika">{t('cont.northAmerica')}</SelectItem>
                    <SelectItem value="Südamerika">{t('cont.southAmerica')}</SelectItem>
                    <SelectItem value="Asien">{t('cont.asia')}</SelectItem>
                    <SelectItem value="Afrika">{t('cont.africa')}</SelectItem>
                    <SelectItem value="Ozeanien">{t('cont.oceania')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">{t('loc.country')}</Label>
                <Input value={editForm.location_country || ''} onChange={e => setEditForm(p => ({...p, location_country: e.target.value}))} className="bg-[#0A0A0B] border-white/10 mt-1 h-9 text-sm" placeholder="Deutschland" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#a1a1aa] text-xs">{t('loc.state')}</Label>
                <Input value={editForm.location_state || ''} onChange={e => setEditForm(p => ({...p, location_state: e.target.value}))} className="bg-[#0A0A0B] border-white/10 mt-1 h-9 text-sm" placeholder="Berlin" />
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">{t('loc.city')}</Label>
                <Input value={editForm.location_city || ''} onChange={e => setEditForm(p => ({...p, location_city: e.target.value}))} className="bg-[#0A0A0B] border-white/10 mt-1 h-9 text-sm" placeholder="Berlin" />
              </div>
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">{t('loc.address')}</Label>
              <Input value={editForm.location_address || ''} onChange={e => setEditForm(p => ({...p, location_address: e.target.value}))} className="bg-[#0A0A0B] border-white/10 mt-1 h-9 text-sm" placeholder="Musterstr. 1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#a1a1aa] text-xs">{t('loc.stationCode')}</Label>
                <Input value={editForm.station_code || ''} onChange={e => setEditForm(p => ({...p, station_code: e.target.value}))} className="bg-[#0A0A0B] border-white/10 mt-1 h-9 text-sm font-mono" placeholder="BER-01" />
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">{t('loc.poiType')}</Label>
                <Select value={editForm.poi_type || 'none'} onValueChange={v => setEditForm(p => ({...p, poi_type: v === 'none' ? '' : v}))}>
                  <SelectTrigger className="bg-[#0A0A0B] border-white/10 mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141416] border-white/10">
                    <SelectItem value="none">{t('poi.none')}</SelectItem>
                    <SelectItem value="airport">{t('poi.airport')}</SelectItem>
                    <SelectItem value="train_station">{t('poi.trainStation')}</SelectItem>
                    <SelectItem value="24_7">{t('poi.247')}</SelectItem>
                    <SelectItem value="important">{t('poi.important')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">{t('loc.notes')}</Label>
              <textarea value={editForm.notes || ''} onChange={e => setEditForm(p => ({...p, notes: e.target.value}))}
                className="w-full mt-1 p-2 bg-[#0A0A0B] border border-white/10 rounded text-sm text-white resize-y min-h-[60px] focus:outline-none focus:border-[#D50C2D]"
                placeholder={`z.B. Schlüssel bei Rezeption, Kontakt: Herr Müller...`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDeviceOpen(null)} className="border-white/10">{t('common.cancel')}</Button>
            <Button onClick={handleSaveDevice} disabled={saving} className="bg-[#D50C2D] hover:bg-[#f21d41]">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
        <DialogContent className="bg-[#141416] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-heading">{t('td.addUserTo')} {tenant.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-[#a1a1aa]">{t('common.name')}</Label><Input value={newUser.name} onChange={e => setNewUser(p => ({...p, name: e.target.value}))} className="bg-[#0A0A0B] border-white/10 mt-1" placeholder="Max Mustermann" /></div>
            <div><Label className="text-[#a1a1aa]">E-Mail</Label><Input type="email" value={newUser.email} onChange={e => setNewUser(p => ({...p, email: e.target.value}))} className="bg-[#0A0A0B] border-white/10 mt-1" placeholder="max@beispiel.de" /></div>
            <div><Label className="text-[#a1a1aa]">Passwort</Label><Input type="password" value={newUser.password} onChange={e => setNewUser(p => ({...p, password: e.target.value}))} className="bg-[#0A0A0B] border-white/10 mt-1" /></div>
            <div><Label className="text-[#a1a1aa]">{t('common.type')}</Label>
              <Select value={newUser.role} onValueChange={v => setNewUser(p => ({...p, role: v}))}>
                <SelectTrigger className="bg-[#0A0A0B] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141416] border-white/10"><SelectItem value="admin">{t('role.admin')}</SelectItem><SelectItem value="technician">{t('role.technician')}</SelectItem><SelectItem value="viewer">{t('role.viewer')}</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddUserOpen(false)} className="border-white/10">{t('common.cancel')}</Button>
            <Button onClick={handleAddUser} disabled={saving} className="bg-[#D50C2D] hover:bg-[#f21d41]">{saving ? <CircleNotch size={16} className="animate-spin" /> : t('common.create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
